import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { isNetworkRequestInFlight } from "@apollo/client/core/networkStatus"

import { useGetQueuedJobsLazyQuery } from "../generated/graphql"

const useCreateContext = () => {
	const mounted = useRef(false)

	const [jobIds, setJobIds] = useState<string[]>([])

	const [getQueuedJobs, { data, networkStatus, stopPolling }] =
		useGetQueuedJobsLazyQuery({
			pollInterval: 2000,
		})

	useEffect(() => {
		mounted.current = true

		return () => {
			mounted.current = false
		}
	}, [])

	useEffect(() => {
		if (mounted.current && jobIds.length > 0) {
			return
		}

		stopPolling && stopPolling()
	}, [jobIds, stopPolling])

	useEffect(() => {
		if (!mounted.current || jobIds.length === 0) {
			return
		}

		getQueuedJobs({
			variables: {
				ids: jobIds,
			},
		})
	}, [getQueuedJobs, jobIds])

	const mappedData = useMemo(() => {
		if (!data) {
			return undefined
		}

		const map = new Map<string, typeof data["jobs"][0]>()

		for (const job of data.jobs) {
			map.set(job.id, job)
		}

		return map
	}, [data])

	return {
		data: mappedData,
		fetching: isNetworkRequestInFlight(networkStatus),
		setJobIds,
	}
}

const QueuedJobsContext = createContext<ReturnType<
	typeof useCreateContext
> | null>(null)

export const useQueuedJobsContext = () => {
	const ctx = useContext(QueuedJobsContext)

	if (ctx === null) {
		throw new Error("No QueuedJobsContextProvider available.")
	}

	return ctx
}

export const QueuedJobsContextProvider: React.FC = ({ children }) => {
	const ctx = useCreateContext()

	return (
		<QueuedJobsContext.Provider value={ctx}>
			{children}
		</QueuedJobsContext.Provider>
	)
}
